<template>
  <v-container fluid class="home-container">
    <v-row>
      <v-col cols="12" sm="12">
        <v-row>
          <v-col cols="2">
            <h4 class="display-1">{{ selectedDate | moment('dddd D MMM') }}</h4>
            <v-btn v-if="!plotMapMode" @click="plotMapMode = true" class="white--text mt-1" color="success" depressed small>
              Plot Map Directions
            </v-btn>

            <v-btn v-if="plotMapMode" @click="plotMapMode = false; mapPostCodes = []" class="white--text mt-1" color="success" depressed small>
              Stop Plotting Map and Clear
            </v-btn>

            <p v-if="plotMapMode">{{ mapPostCodes.toString() }}</p>

            <v-btn v-if="plotMapMode && mapPostCodes.length > 0" @click="generateGoogleMap" class="white--text mt-1" color="primary" depressed small>
              Generate Directions
            </v-btn>

            <div v-if="contractSend">
              <p v-for="(t, i) in contractsToSend" :key="i">{{ t.name }}</p>
            </div>
            <v-btn small v-if="contractSend && contractsToSend.length > 0" :disabled="sendingContracts" :loading="sendingContracts" @click="sendContracts" class="white--text mt-3" color="primary" depressed>
              Confirm Send Contracts
            </v-btn>
          </v-col>
          <v-col cols="1">
            <v-menu ref="menu" v-model="menu" :close-on-content-click="false" transition="scale-transition" offset-y
                    min-width="290px">
              <template v-slot:activator="{ on }">
                <v-text-field outlined dense :value="computedDateFormattedMomentjs" label="Selected Date" readonly v-on="on"
                              clearable/>
              </template>
              <v-date-picker first-day-of-week="1" v-model="selectedDate" no-title scrollable></v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="2">
            <v-autocomplete
                dense
                v-model="selectedBooking"
                :items="searchedBookings"
                :loading="isLoading"
                :search-input.sync="searchBookings"
                color="primary"
                hide-no-data
                item-text="display"
                placeholder="Start typing to Search"
                return-object
                clearable
                no-filter
                outlined
                append-icon="mdi-calendar-search"
            />
          </v-col>
          <v-col cols="5">
            <v-btn @click="setToToday" color="primary" small class="mr-2">Today</v-btn>
            <v-btn @click="locationSearch = true" class="white--text mr-2" color="secondary" depressed small>
              Location Search
            </v-btn>
            <v-btn @click="sendContractState" class="white--text mr-2" color="warning" depressed small>
              Send Contracts
            </v-btn>
            <v-badge v-if="upcomingRepeats.length > 0" bordered color="error"
                     :content="upcomingRepeats.length" :value="upcomingRepeats.length" overlap>
              <v-btn @click="viewUpcomingRepeats" class="white--text mr-2" color="info" depressed small>
                Repeats
              </v-btn>
            </v-badge>
            <v-badge v-if="reschedule.length > 0" bordered color="error"
                     :content="reschedule.length" :value="reschedule.length" overlap>
              <v-btn @click="rescheduleDialog = true" class="white--text mr-2" color="error" depressed small>
                Reschedule
              </v-btn>
            </v-badge>

            <v-badge v-if="cancellation.length > 0" bordered color="error"
                     :content="cancellation.length" :value="cancellation.length" overlap>
              <v-btn @click="cancellationDialog = true" class="white--text mr-2" color="warning" depressed small>
                Cancelled
              </v-btn>
            </v-badge>
          </v-col>
          <v-col cols="2">
            <div style="width: 100%" class="text-right">
              <p class="header-p-home subtitle-1" @click="bookingsTodayDialog = true">Bookings Value Today: {{ accounting.formatMoney(dailyValue, '£', 2) }}</p>
              <p class="header-p-home subtitle-1" @click="salesTodayDialog = true">Sales Today: {{ accounting.formatMoney(dailySales, '£', 2) }}</p>
              <p class="header-p-home subtitle-1">Cumulative: {{ accounting.formatMoney(cumulative.turnover, '£', 2) }}</p>
            </div>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="12" sm="12">
        <DxScheduler
            v-if="renderComponent"
            :data-source="parsedBookings"
            :current-date="selectedDate"
            :views="views"
            :height="1200"
            :first-day-of-week="1"
            :start-day-hour="6"
            :end-day-hour="23"
            current-view="day"
            :show-all-day-panel="false"
            resource-cell-template="resourceCellTemplate"
            appointment-cell-template="appointmentCellTemplate"
            :groups="groups"
            time-zone="Europe/London"
            v-on:appointment-dbl-click="appointmentDoubleClick"
            v-on:cell-click="cellClick"
            v-on:appointment-click="appointmentClick"
            v-on:appointment-updating="onAppointmentRendered"
            v-on:appointment-updated="appointmentMoved"
            :on-appointment-context-menu="onAppointmentContextMenu"
            @contextmenu.prevent="onAppointmentContextMenu"
            @initialized="saveGridInstance"
        >
          <DxResource :allow-multiple="false" :data-source="parsedTechnicians" label="Technician" field-expr="valetId"/>

          <template #resourceCellTemplate="technician">
            <ResourceCell :select-post-code="plotMapMode" :select-technician="contractSend" :technician="technician.data"/>
          </template>

          <template v-slot:appointmentTemplate="cellData">
            <DataCell :select-post-code="plotMapMode" :cell-data="cellData" :entries="entries" />
          </template>
        </DxScheduler>
      </v-col>
    </v-row>

    <v-dialog v-model="locationSearch" persistent width="40%">
      <v-card>
        <v-card-title class="headline">Search for bookings by postcode</v-card-title>
        <v-card-text>
          <v-text-field v-model="searchPostCode" label="Enter Postcode to search by" />
          <v-btn :disabled="searchPostCode.length === 0" @click="doLocationSearchFuture" color="secondary">Search Future</v-btn>
          <v-btn :disabled="searchPostCode.length === 0" @click="doLocationSearchPast" color="info" class="ml-3">Search Past</v-btn>
          <div v-if="nearByBookings.length > 0">
            <v-data-iterator
                :items="nearByBookings"
            >
              <template v-slot:default="props">
                <v-row>
                  <v-col
                      v-for="item in props.items"
                      :key="item._id"
                      cols="12"
                  >
                    <v-card>
                      <v-card-title>{{item._id}} <span class="ml-2"> - Distance {{ (item.calculated / 1609).toFixed(1) }} miles</span></v-card-title>
                      <v-card-text>
                        <p>Date: {{item.startDate | moment('DD/MM/YYYY HH:mm')}}</p>
                        <p>Service: {{item.service}}</p>
                        <p v-if="item.phone">Phone: {{item.phone}}</p>
                        <p v-if="item.email">Email: {{item.email}}</p>
                        <p>Value: £{{item.sum}}</p>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </template>
            </v-data-iterator>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="locationSearch = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="rescheduleDialog" persistent width="40%">
      <v-card>
        <v-card-title class="headline">Bookings to Reschedule</v-card-title>
        <v-card-text>
          <v-data-iterator
              :items="reschedule"
          >
            <template v-slot:default="props">
              <v-row>
                <v-col
                    v-for="item in props.items"
                    :key="item._id"
                    cols="12"
                >
                  <v-card>
                    <v-card-title>{{item.customer.name}}</v-card-title>
                    <v-card-text>
                      <p>Date: {{item.startDate | moment('DD/MM/YYYY HH:mm')}}</p>
                      <p v-if="item.phone">Phone: {{item.phone}}</p>
                      <p>Value: £{{item.price}}</p>
                    </v-card-text>
                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn color="success" @click="openBooking(item)">View Booking</v-btn>
                    </v-card-actions>
                  </v-card>
                </v-col>
              </v-row>
            </template>
          </v-data-iterator>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="rescheduleDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="cancellationDialog" persistent width="40%">
      <v-card>
        <v-card-title class="headline">Cancelled Bookings</v-card-title>
        <v-card-text>
          <v-data-iterator
              :items="cancellation"
          >
            <template v-slot:default="props">
              <v-row>
                <v-col
                    v-for="item in props.items"
                    :key="item._id"
                    cols="12"
                >
                  <v-card>
                    <v-card-title>{{item.customer.name}}</v-card-title>
                    <v-card-text>
                      <p>Date: {{item.startDate | moment('DD/MM/YYYY HH:mm')}}</p>
                      <p v-if="item.phone">Phone: {{item.phone}}</p>
                      <p>Value: £{{item.price}}</p>
                    </v-card-text>
                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn color="success" @click="openBooking(item)">View Booking</v-btn>
                      <v-btn color="error" @click="setTheActiveBookingAndDelete(item)"><v-icon>mdi-delete</v-icon></v-btn>
                    </v-card-actions>
                  </v-card>
                </v-col>
              </v-row>
            </template>
          </v-data-iterator>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancellationDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="bookingsTodayDialog" persistent width="40%">
      <v-card>
        <v-card-title class="headline">Todays Bookings</v-card-title>
        <v-card-text>
          <v-data-iterator
              :items="b"
          >
            <template v-slot:default="props">
              <v-row>
                <v-col
                    v-for="item in props.items"
                    :key="item._id"
                    cols="12"
                >
                  <v-card>
                    <v-card-title>{{item.customer.name}}</v-card-title>
                    <v-card-text>
                      <p>Date: {{item.startDate | moment('DD/MM/YYYY HH:mm')}}</p>
                      <a v-if="item.isXeroBooking" :href="item.xeroInvoiceLink" target="_blank">Invoice: {{item.xeroInvoiceNumber}}</a>
                      <p v-if="item.phone">Phone: {{item.phone}}</p>
                      <p>Value: £{{item.price}}</p>
                    </v-card-text>
                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn color="success" @click="openBooking(item)">View Booking</v-btn>
                    </v-card-actions>
                  </v-card>
                </v-col>
              </v-row>
            </template>
          </v-data-iterator>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="bookingsTodayDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="salesTodayDialog" persistent width="40%">
      <v-card>
        <v-card-title class="headline">Todays Sales</v-card-title>
        <v-card-text>
          <v-data-iterator
              :items="dailySalesBookings"
          >
            <template v-slot:default="props">
              <v-row>
                <v-col
                    v-for="item in props.items"
                    :key="item._id"
                    cols="12"
                >
                  <v-card>
                    <v-card-title>{{item.booking.customer.name}}</v-card-title>
                    <v-card-text>
                      <p>Date: {{item.booking.startDate | moment('DD/MM/YYYY HH:mm')}}</p>
                      <div>
                        <a v-if="item.booking.isXeroBooking && item.bookingHistory && item.bookingHistory.length > 0" :href="item.bookingHistory[0].xeroInvoiceLink" target="_blank">Invoice: {{item.bookingHistory[0].xeroInvoiceNumber}}</a>
                      </div>

                      <p>Value: £{{item.booking.price}}</p>
                      <p>Made By: {{item.booking.bookingMadeBy}}</p>
                    </v-card-text>
                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn color="success" @click="openBooking(item.booking)">View Booking</v-btn>
                    </v-card-actions>
                  </v-card>
                </v-col>
              </v-row>
            </template>
          </v-data-iterator>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="salesTodayDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
import moment from 'moment';
import {DxScheduler, DxResource} from 'devextreme-vue/scheduler';
import ResourceCell from './scheduler/ResourceCell.vue';
import {BookingsService} from "@/services/BookingsService";
import DataCell from "./scheduler/DataCell";
import {mapState, mapActions, mapMutations} from "vuex";
import {BookingsHttp} from "@/http/BookingsHttp";
// import {XeroHttp} from "@/http/XeroHttp";
import accounting from 'accounting';
import EventBus from "@/EventBus";
import {XeroHttp} from "@/http/XeroHttp";

export default {
  name: "Bookings",
  components: {DataCell, ResourceCell, DxScheduler, DxResource},
  data() {
    return {
      views: ['day'],
      groups: ['valetId'],
      menu: false,
      selectedDate: new Date().toISOString().substr(0, 10),
      parsedBookings: [],
      parsedTechnicians: [],
      renderComponent: true,
      selectedBooking: {},
      searchedBookings: [],
      searchBookings: '',
      isLoading: false,
      accounting: accounting,
      mapPostCodes: [],
      plotMapMode: false,
      locationSearch: false,
      searchPostCode: '',
      nearByBookings: [],
      contractSend: false,
      contractsToSend: [],
      sendingContracts: false,
      rescheduleDialog: false,
      cancellationDialog: false,
      bookingsTodayDialog: false,
      salesTodayDialog: false,
      instance: null
    }
  },
  computed: {
    ...mapState("bookings", ["bookings","dailySalesBookings", "dailyValue", "dailySales", "entries", "reschedule", "cancellation", "cumulative"]),
    ...mapState("xero", ["services", "periods", "technicians", "accounts"]),
    ...mapState("technicians", ["appTechnicians"]),
    ...mapState("repeats", ["upcomingRepeats"]),
    computedDateFormattedMomentjs() {
      return this.selectedDate ? moment(this.selectedDate).format('DD/MM/YYYY') : ''
    },
    b: {
      get() {
        return this.bookings;
      }
    },
    t: {
      get() {
        return this.appTechnicians;
      }
    },
  },
  methods: {
    ...mapActions("bookings", ["setBookings", "setCumulative", "setManageBookingDialog", "setActiveBooking", "setPreviewJobDialog", "setManageDeleteBookingDialog"]),
    ...mapActions("repeats", ["setUpcomingRepeatsDialog", "setActiveRepeat", "setManageRepeatDialog"]),
    ...mapMutations("app", ["setToast"]),
    ...mapActions("technicians", ["setTechnicians"]),
    saveGridInstance(e) {
      this.instance = e.component;
    },
    valet(valetId) {
      return this.services.find(x => x.code === valetId).description;
    },
    async setTheActiveBookingAndDelete(item) {
      await this.setActiveBooking(item);
      await this.setManageDeleteBookingDialog(true);
    },
    setToToday() {
      this.selectedDate = new Date().toISOString().substr(0, 10);
    },
    sendContractState() {
      this.contractSend = !this.contractSend;
    },
    async doLocationSearchFuture() {
      this.nearByBookings = [];
      const { data } = await BookingsHttp.getBookingsByDistance(new Date(), this.searchPostCode, false);
      // // eslint-disable-next-line no-console
      // console.log(data);
      this.closeBookings = true;
      this.nearByBookings = data;
    },
    async doLocationSearchPast() {
      this.nearByBookings = [];
      const { data } = await BookingsHttp.getBookingsByDistance(new Date(), this.searchPostCode, true);
      // // eslint-disable-next-line no-console
      // console.log(data);
      this.closeBookings = true;
      this.nearByBookings = data;
    },
    async viewUpcomingRepeats() {
      await this.setUpcomingRepeatsDialog(true);
    },
    async appointmentDoubleClick(item) {
      item.cancel = true;
      if (item.targetedAppointmentData.booking && !item.targetedAppointmentData.booking.isRepeat && !item.targetedAppointmentData.booking.isEntry) {
        // eslint-disable-next-line no-console
        // console.log(item.targetedAppointmentData.booking);
        await this.setActiveBooking(item.targetedAppointmentData.booking);
      }
      await this.manageBooking();
    },
    onAppointmentRendered(data) {
      if (data.oldData.booking.isApproved) {
        data.cancel = true;
      }
    },
    async cellClick(item) {
      item.cancel = true;
      await this.setActiveBooking({
        startDate: item.cellData.startDate,
        endDate: item.cellData.endDate,
        isXeroBooking: true,
        isNewBookingSystem: true,
        customer: {},
        xeroValetTrackingId: this.parsedTechnicians.find(x => x.id === item.cellData.groups.valetId).text
      });
      await this.manageBooking();
    },
    async appointmentClick(item) {
      item.cancel = true;
    },
    async onAppointmentContextMenu(item) {
      item.cancel = true;
      if (item.targetedAppointmentData.booking && !item.targetedAppointmentData.booking.isRepeat) {
        await this.setActiveBooking(item.targetedAppointmentData.booking);
      }
      await this.setPreviewJobDialog(true);
    },
    handler: function (e) {
      //do stuff
      e.preventDefault();
    },
    async manageBooking() {
      await this.setManageBookingDialog(true);
    },
    async appointmentMoved(item) {
      if (item.appointmentData.booking.isApproved) {
        item.cancel = true;
        this.setToast({color: 'error', text: 'Booking cannot be moved - it has been locked', show: true})
        item.appointmentData.startDate = item.appointmentData.booking.startDate;
        item.appointmentData.endDate = item.appointmentData.booking.endDate;
      } else if(item.appointmentData.booking.isEntry) {
        item.cancel = true;
        this.setToast({color: 'error', text: 'Booking cannot be moved - it is a blocked entry', show: true})
        item.appointmentData.startDate = item.appointmentData.booking.startDate;
        item.appointmentData.endDate = item.appointmentData.booking.endDate;
      } else {
        try {
          const booking = item.appointmentData.booking;
          booking.valetId = item.appointmentData.valetId;
          booking.xeroValetTrackingId = this.t.find(x => x._id === item.appointmentData.valetId).name;
          booking.startDate = item.appointmentData.startDate;
          booking.endDate = item.appointmentData.endDate;
          booking.isNewBookingSystem = true;
          await BookingsHttp.updateBooking(booking);

          if (booking.parentBookingId) {
            const parent = await BookingsHttp.getSingleBooking(booking.parentBookingId).then(result => result.data);
            parent.selectedServices[parent.selectedServices.findIndex(x => x.bookingId === booking._id)].xeroValetTrackingId = this.t.find(x => x._id === item.appointmentData.valetId).name;
            await BookingsHttp.updateBooking(parent);
          }

          // if (booking.primaryTrackingId) {
          //   await XeroHttp.splitInvoice(booking);
          // } else

          if (booking.isXeroBooking) {
              if ((booking.selectedServices && booking.selectedServices.length > 0 && booking.isParent) || !booking.selectedServices) {
                await XeroHttp.updateInvoice(booking);
              }
          }
          await this.setBookings(this.selectedDate);
          await this.setCumulative(this.selectedDate);
          await this.setTechnicians();
          this.setToast({color: 'success', text: 'Booking updated successfully', show: true});
        } catch (err) {
          if (err.response) {
            this.setToast({color: 'error', text: err.response.data.error, show: true})
          } else {
            this.setToast({color: 'error', text: err, show: true})
          }
        }
      }

    },
    forceRerender() {
      this.renderComponent = false;
      this.$nextTick(() => {
        this.renderComponent = true;
      });
    },
    updateSelectedDate(date) {
      this.selectedDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().substr(0, 10);
    },
    generateGoogleMap() {
      const origin = this.mapPostCodes[0];
      const end = this.mapPostCodes[this.mapPostCodes.length -1];
      let waypoints = "";
      if (this.mapPostCodes.length > 2) {
        this.mapPostCodes.forEach((item, index) => {
          if (index !== 0 && index !== this.mapPostCodes.length -1) {
            if (waypoints === "") {
              waypoints = `${item}`
            } else {
              waypoints = `${waypoints}|${item}`;
            }
          }
        });
        window.open(`https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${end}&waypoints=${waypoints}&travelmode=driving`, "_blank");
      } else {
        window.open(`https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${end}&travelmode=driving`, "_blank");
      }
    },
    async sendContracts() {
      const ids = [];
      this.contractsToSend.forEach(c => ids.push(c.id));
      try {
        this.sendingContracts = true;
        await BookingsHttp.sendContracts(ids, this.selectedDate);
        this.setToast({color: 'success', text: 'Contracts sent successfully', show: true});
        this.contractsToSend = [];
        this.contractSend = false;
      } catch (err) {
        if (err.response) {
          this.setToast({color: 'error', text: err.response.data.error, show: true})
        } else {
          this.setToast({color: 'error', text: err, show: true})
        }
      } finally {
        this.sendingContracts = false;
      }
    },
    async openBooking(item) {
      await this.setActiveBooking(item);
      await this.setManageBookingDialog(true);
    }
  },
  watch: {
    async selectedBooking(val) {
      await this.setActiveBooking(val);
      await this.manageBooking();
    },
    async searchBookings(val) {
      if (val && val.length < 5) return;
      if (val) {
        this.isLoading = true;
        try {
          const temp = await BookingsHttp.searchForBookings(this.searchBookings).then(result => result.data);
          temp.map(t => {
            if (t.isXeroBooking && t.customer) {
              t.display = `${t.customer.name} - ${this.$moment(t.startDate).format('DD/MM/YYYY')}`;
            } else if (t.customer) {
              t.display = `${t.customer.name} - ${this.$moment(t.startDate).format('DD/MM/YYYY')}`;
            }
          });
          this.searchedBookings = temp;
        } catch (e) {
          alert(e);
        } finally {
          this.isLoading = false;
        }
      }
    },
    async selectedDate(newVal) {
      await this.setBookings(newVal);
      await this.setCumulative(newVal);
      await this.setTechnicians();
      this.forceRerender();
    },
    b: {
      handler(newVal) {
        this.parsedBookings = [];
        newVal.forEach(v => {
          this.parsedBookings.push({
            text: v.customer.name,
            startDate: new Date(v.startDate),
            endDate: new Date(v.endDate),
            valetId: v.valetId,
            color: BookingsService.getColour(v),
            booking: v
          });
        });
      },
      deep: true,
      immediate: true
    },
    t: {
      handler(newVal) {
        this.parsedTechnicians = [];

        newVal.forEach(v => {
          if (v.name !== "Reschedule" && v.name !== "Cancellation") {
            const techData = this.cumulative.commission;
            const tech = techData.find(x => x.tech === v.name);
            let total = 0;
            let turnover = 0;
            if (tech) {
              total = tech.total;
              turnover = tech.sum
            }
            this.parsedTechnicians.push({
              text: v.name,
              id: v._id,
              region: v.region,
              commission: total,
              turnover: turnover
            });
          }
        });
      },
      deep: true,
      immediate: true
    }
  },
  mounted() {
    const self = this;
    window.addEventListener('keydown', function (e) {
      if (e.key === "ArrowRight") {
        const localDate = self.$moment(self.selectedDate).add(1, 'days').toDate();
        self.updateSelectedDate(localDate);
      } else if (e.key === "ArrowLeft") {
        const localDate = self.$moment(self.selectedDate).subtract(1, 'days').toDate();
        self.updateSelectedDate(localDate);
      }
    }, false);

    EventBus.$on('set-scheduler-date', (date) => {
      this.selectedDate = new Date(date).toISOString().substr(0, 10);
    });

    EventBus.$on('updateBookings', async () => {
      await this.setBookings(this.selectedDate);
      await this.setCumulative(this.selectedDate);
      await this.setTechnicians();
    });

    EventBus.$on("postcode", postcode => {
      this.mapPostCodes.push(postcode);
    });

    EventBus.$on("technician", technician => {
      const index = this.contractsToSend.findIndex(x => x.id === technician.id);
      if (index === -1) {
        this.contractsToSend.push({name: technician.text, id: technician.id});
      }
    });

    setTimeout(() => {
      self.forceRerender();
    }, 5000);
  }
}
</script>

<style>
@-moz-document url-prefix() {
  .dx-scheduler-work-space-month .dx-scheduler-date-table-cell {
    position: relative;
  }

  .dx-scheduler-work-space-month .dx-scheduler-date-table-cell .disable-date {
    position: absolute;
    width: 100%;
    height: 100%;
  }
}

.disable-date,
.dinner {
  height: 100%;
}
.disable-date {
  background-image: repeating-linear-gradient(135deg,
  rgba(244, 67, 54, 0.1),
  rgba(244, 67, 54, 0.1) 4px,
  transparent 4px,
  transparent 9px);
  color: #9B6467;
}

.dx-scheduler-header-panel-cell .disable-date {
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.dx-scheduler-date-table-other-month.dx-scheduler-date-table-cell {
  opacity: 1;
  color: rgba(0, 0, 0, 0.3);
}

.dx-scheduler-date-table-cell, .dx-template-wrapper {
  position: relative;
  height: 100%;
}

.dx-scheduler-date-table-cell .dx-template-wrapper {
  position: absolute;
  width: 100%;
  height: 100%;
}

.dx-scheduler-navigator .dx-button {
  display: none !important;
}

.dx-scheduler-view-switcher-label {
  display: none !important;
}

.dx-scheduler-view-switcher.dx-dropdownmenu.dx-button-has-icon:not(.dx-button-has-text) {
  display: none !important;
}

.dx-scheduler-date-time-indicator {
  background-color: #027fb8 !important;
}

.dx-scheduler-header {
  height: 0px !important;
}

.home-container {
  margin-top: -15px !important;
}

.header-p-home {
  margin-bottom: 2px !important;
  line-height: 1.2rem !important;
}
</style>
